import axios from 'axios';
import { useEffect, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { AccountInfoBox } from '../../components/Form/AccountInfoBox';
import { FormValidationContainer, useFormValidationContainer } from '../../components/Form/FormValidatorContainer';
import { company, Icon, person } from '../../components/Icon/Icon';
import { useConfig } from '../../config';
import { useTranslation } from '../../i18n/i18n';
import { Res, useApi } from '../../state/api';
import { useSession } from '../../state/session';
import { ApiActionResponse, ApiInvoiceAddress, PaymentMethodType } from '../../types';
import { AccountPageNavigation } from '../AccountPageNavigation/AccountPageNavigation';
import { UiContainer } from '../../state/ui';
import { AlertCollection, AlertMessages, useAlerts } from '../AlertMessages/AlertMessages';
import { ContentCard } from '../ContentCard/ContentCard';
import { EditableCard } from '../EditableCard/EditableCard';
import { Button } from '../Form/Button';
import { ChangePasswordInput, ChangePasswordModel } from '../Form/ChangePasswordInput';
import { CustomerInput, CustomerModel, CustomerSummary } from '../Form/CustomerInput';
import { CustomerTypeInput, CustomerTypeModel } from '../Form/CustomerTypeInput';
import { PaymentInput, PaymentMethodModel, PaymentSummary } from '../Form/PaymentInput';
import { Main } from '../Main/Main';
import { Routing } from '../Routes';
import { AccountDeliveryAddresses } from './AccountDeliveryAddresses';
import './AccountPage.css';
import { ErrorPage } from './ErrorPage';
import { LoaderPage } from './LoaderPage';

const axiosConf = { withCredentials: true };

type PatchOp = { op: string; path: string; value: string | number | boolean };
function buildPatch<T extends { [key: string]: string | number }>(original: T, changed: T): PatchOp[] {
	return Object.keys(original)
		.filter((key) => changed[key] !== original[key] && changed[key] !== undefined)
		.map((key) => ({
			op: 'replace',
			path: '/' + key,
			value: changed[key]!,
		}));
}

function PaymentInputWithoutCreditDetails() {
	return <PaymentInput withoutCardDetails />;
}

export const AccountPage = (): JSX.Element => {
	const t = useTranslation().accountPage;
	const tForm = useTranslation().form;
	const tRoutes = useTranslation().routes;
	const history = useHistory();
	const { logout, user } = useSession();
	const ui = UiContainer.useContainer();
	const { api } = useConfig();
	const { data: customer, error: customerError, mutate: mutateCustomer } = useApi(Res.customer, {});
	const {
		data: invoiceAddresses,
		error: invoiceAddressesError,
		mutate: mutateInvoiceAddresses,
		isLoading: invoiceAddressesIsLoading,
	} = useApi(Res.invoiceAddresses, {});
	const shouldFetchDeliveryAddresses = customer?.type === 'company';
	const {
		data: deliveryAddresses,
		error: deliveryAddressesError,
		mutate: mutateDeliveryAddresses,
		isLoading: deliveryAddressesIsLoading,
	} = useApi(shouldFetchDeliveryAddresses ? Res.deliveryAddresses : null, {});

	const form = useFormValidationContainer();

	const hasSetFormStateRef = useRef(false);
	useEffect(() => {
		if (typeof invoiceAddresses !== 'undefined' && invoiceAddresses.length > 0 && !invoiceAddressesIsLoading) {
			form.paymentType.setValue(invoiceAddresses[0].preferredPaymentType);
			form.paymentEmail.setValue(invoiceAddresses[0].email);
			form.paymentOrgnr.setValue(invoiceAddresses[0].dob);
			form.paymentCompany.setValue(invoiceAddresses[0].company);
			form.paymentRef1.setValue(invoiceAddresses[0].ref1);
			form.paymentStreet.setValue(invoiceAddresses[0].street);
			form.paymentCareOf.setValue(invoiceAddresses[0].careOf);
			form.paymentZip.setValue(invoiceAddresses[0].zip);
			form.paymentCity.setValue(invoiceAddresses[0].city);
			form.paymentIdentifier.setValue(invoiceAddresses[0].einvoiceIdentifier);
			form.paymentOperator.setValue(invoiceAddresses[0].einvoiceOperator);
			hasSetFormStateRef.current = true;
		}
	}, [invoiceAddresses, invoiceAddressesIsLoading]);

	const customerTypeAlerts = useAlerts();
	const customerAlerts = useAlerts();
	const passwordAlerts = useAlerts();
	const paymentAlerts = useAlerts();

	if (customerError || invoiceAddressesError || deliveryAddressesError) return <ErrorPage />;

	if (
		customer === undefined ||
		invoiceAddresses === undefined ||
		(shouldFetchDeliveryAddresses && (deliveryAddresses === undefined || deliveryAddressesIsLoading))
	) {
		return <LoaderPage />;
	}

	const customerType = {
		customerType: customer.type,
	};
	const passwordModel = {
		password: '',
		newPassword: '',
		newPassword2: '',
		isValid: false,
	};
	const invoiceAddress: ApiInvoiceAddress = invoiceAddresses[0] || {
		id: undefined,
		preferredPaymentType: 'pdf',
		email: '',
		dob: '',
		company: '',
		ref1: '',
		ref2: '',
		careOf: '',
		street: '',
		zip: '',
		city: '',
		einvoiceIdentifier: '',
		einvoiceOperator: '',
	};
	const paymentMethodModel: PaymentMethodModel = {
		type: invoiceAddress.preferredPaymentType as PaymentMethodType,
		invoiceEmail: invoiceAddress.email,
		orgnr: invoiceAddress.dob,
		company: invoiceAddress.company,
		ref1: invoiceAddress.ref1,
		street: invoiceAddress.street,
		careOf: invoiceAddress.careOf,
		zip: invoiceAddress.zip,
		city: invoiceAddress.city,
		einvoiceIdentifier: invoiceAddress.einvoiceIdentifier,
		einvoiceOperator: invoiceAddress.einvoiceOperator,
	};

	async function sendPatch(
		endpoint: string,
		payload: unknown,
		alerts: AlertCollection,
		successMessage: string
	): Promise<boolean> {
		alerts.clear();
		try {
			await axios.patch(`${api}${endpoint}`, payload, axiosConf);
			alerts.set({ type: 'success', message: successMessage });
			return true;
		} catch (e) {
			alerts.set({ type: 'error', message: t.couldNotUpdateInfo });
			return false;
		}
	}

	async function sendPost(
		endpoint: string,
		payload: unknown,
		alerts: AlertCollection,
		successMessage: string
	): Promise<boolean> {
		alerts.clear();
		try {
			await axios.post(`${api}${endpoint}`, payload, axiosConf);
			alerts.set({ type: 'success', message: successMessage });
			return true;
		} catch (e) {
			alerts.set({ type: 'error', message: t.couldNotUpdateInfo });
			return false;
		}
	}

	async function submitCustomerType(model: CustomerTypeModel) {
		const payload = buildPatch({ type: customer!.type }, { type: model.customerType });
		const saved = await sendPatch('me/medium.json', payload, customerTypeAlerts, t.customerTypeSaved);
		// the card closes in place, so the plausible tag has to learn the new type from here
		if (saved) ui.setCustomerType(model.customerType);
		mutateCustomer();
	}

	async function submitCustomer(model: CustomerModel) {
		const renamedObj = {
			email: model.email,
			company: model.company,
			phone: model.phone,
			firstname: model.firstname,
			lastname: model.lastname,
		};
		const payload = buildPatch(customer!, renamedObj);
		await sendPatch('me/medium.json', payload, customerAlerts, t.informationUpdated);
		mutateCustomer();
	}

	async function submitPaymentMethod(model: PaymentMethodModel) {
		const renamedObj: Partial<ApiInvoiceAddress> = {
			preferredPaymentType: form.paymentType.value,
			email: form.paymentEmail.value,
			company: form.paymentCompany.value,
			careOf: form.paymentCareOf.value,
			dob: form.paymentOrgnr.value,
			ref1: form.paymentRef1.value,
			street: form.paymentStreet.value,
			zip: form.paymentZip.value,
			city: form.paymentCity.value,
			einvoiceIdentifier: form.paymentIdentifier.value,
			einvoiceOperator: form.paymentOperator.value,
		};
		const payload = buildPatch(invoiceAddress! as Partial<ApiInvoiceAddress>, renamedObj);

		const id = invoiceAddress.id;
		if (id === undefined) {
			await sendPost('me/invoice-addresses.json', model, paymentAlerts, t.chosenPaymentSaved);
		} else {
			await sendPatch(`me/invoice-addresses/${id}.json`, payload, paymentAlerts, t.chosenPaymentSaved);
		}
		mutateInvoiceAddresses();
	}

	async function submitPassword(payload: ChangePasswordModel) {
		const currentPassword = payload.password;
		const newPassword = payload.newPassword;
		passwordAlerts.clear();
		if (payload.isValid) {
			try {
				const res = await axios.post<ApiActionResponse>(
					`${api}me/password.json`,
					{ currentPassword, newPassword },
					axiosConf
				);
				if (res.data.success) {
					passwordAlerts.set({
						type: 'success',
						message: t.passwordHasBeenUpdated,
					});
				} else {
					passwordAlerts.set({
						type: 'error',
						message: t.currentPasswordNotCorrect,
					});
				}
			} catch (e) {
				passwordAlerts.set({
					type: 'error',
					message: t.couldNotUpdatePassword,
				});
			}
		} else {
			passwordAlerts.set({
				type: 'error',
				message: t.allFieldsNotCorrectlyFilled,
			});
		}
	}

	async function signout() {
		if (await logout()) {
			history.push('/');
			history.go(0);
		}
	}

	return (
		<Main>
			<AccountPageNavigation />
			<h1 className="private-page-heading">{t.settings}</h1>
			<EditableCard<CustomerTypeModel>
				title={t.customerType}
				form={CustomerTypeInput}
				model={customerType}
				onSubmit={submitCustomerType}
			>
				<AlertMessages alerts={customerTypeAlerts} />
				<AccountInfoBox>
					{customerType.customerType === 'company' ? <Icon shape={company} /> : <Icon shape={person} />}
					<span className="ml-1">
						{
							{
								company: tForm.company,
								private: tForm.person,
								'': '',
							}[customerType.customerType]
						}
					</span>
				</AccountInfoBox>
			</EditableCard>
			<EditableCard<CustomerModel>
				title={t.yourInfo}
				form={CustomerInput}
				model={customer}
				onSubmit={submitCustomer}
			>
				<AlertMessages alerts={customerAlerts} />
				<CustomerSummary customer={customer} />
			</EditableCard>
			{customer.type === 'company' && user?.storedAddresses && (
				<AccountDeliveryAddresses
					deliveryAddresses={deliveryAddresses ?? []}
					mutateDeliveryAddresses={mutateDeliveryAddresses}
				/>
			)}
			<EditableCard<PaymentMethodModel>
				title={t.paymentMethod}
				form={PaymentInputWithoutCreditDetails}
				model={paymentMethodModel}
				onSubmit={submitPaymentMethod}
			>
				<AlertMessages alerts={paymentAlerts} />
				<PaymentSummary model={paymentMethodModel} />
			</EditableCard>
			<EditableCard<ChangePasswordModel>
				title={t.password}
				form={ChangePasswordInput}
				model={passwordModel}
				onSubmit={submitPassword}
				validator={(model) => {
					if (model.isValid) return true;
					passwordAlerts.set({
						type: 'error',
						message: t.allFieldsNotCorrectlyFilled,
					});
					return false;
				}}
			>
				<AlertMessages alerts={passwordAlerts} />
				<AccountInfoBox>
					<dl>
						<dd>******</dd>
					</dl>
				</AccountInfoBox>
			</EditableCard>
			<ContentCard title={t.communication}>
				<p>
					{t.clickHereForCommunicationSettings} <Link to={tRoutes.communication}>{t.clickHere}</Link>.
				</p>
			</ContentCard>
			<ContentCard title={t.removeAccount}>
				<p className="remove-account-text">
					{t.ifYouWishToRemoveAccount} <Link to={tRoutes.removeAccount}>{t.clickHere}</Link>.
				</p>
			</ContentCard>
			<ContentCard title={t.signOut}>
				<p>{t.clickOnbuttonBelow}</p>
				<Button className="primary" onClick={signout}>
					{t.signOut}
				</Button>
			</ContentCard>
		</Main>
	);
};

export const accountRouting: Routing = {
	path(urls) {
		return urls.account;
	},
	component: () => {
		return (
			<FormValidationContainer.Provider>
				<AccountPage />
			</FormValidationContainer.Provider>
		);
	},
	requiresAuth: true,
};
